import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { Country } from 'models/country';
import { formatOrganizationNumber, isValidOrganizationNumber } from 'utils/country-helper';

type OrganizationNumberInputProps = {
    country: Country;
    roleCheck?: boolean;
    onValueChange?: (value: string, isOkay: boolean) => void;
} & TextFieldProps;

const OrganizationNumberInput: FC<OrganizationNumberInputProps> = ({
    country,
    roleCheck,
    label,
    onValueChange,
    ...props
}: OrganizationNumberInputProps) => {
    const [value, setValue] = useState<string>('');
    const [currentCountry, setCurrentCountry] = useState<Country>(country);
    const { value: _, ...remainingProps } = props;

    useEffect(() => {
        setValue(props.value as string);
    }, [props.value]);

    useEffect(() => {
        setCurrentCountry(country);
    }, [country]);

    const checkOrganizationNumber = (organizationNumber: string) => {
        setValue(organizationNumber);
        if (roleCheck) {
            return organizationNumber;
        }
        // DD-3700: the format and, where the country has one, the check digit.
        if (!isValidOrganizationNumber(organizationNumber, currentCountry.alpha2Code)) {
            return '';
        }
        const formattedOrganizationNumber = formatOrganizationNumber(organizationNumber, currentCountry.alpha2Code);
        if (formattedOrganizationNumber !== organizationNumber) {
            setValue(formattedOrganizationNumber);
        }
        return formattedOrganizationNumber;
    };

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const organizationNumber = event.target.value;
        const orgNumber = checkOrganizationNumber(organizationNumber);
        const isOkay = !!orgNumber;
        if (onValueChange) {
            onValueChange(orgNumber || organizationNumber, isOkay);
        }
    };

    return <TextField label={label} value={value} {...remainingProps} onChange={onInputChange} type="text" />;
};

export default OrganizationNumberInput;
